import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, Card, CardContent, Grid, Icon, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import { Circle } from '@mui/icons-material'
import HelpIcon from '@mui/icons-material/Help'
import { isNil, uniq } from 'lodash'
import Input from 'common/components/form/Input'
import Select from 'common/components/form/Select'
import Autocomplete from 'common/components/form/Autocomplete'
import { CardTitle } from 'common/components/card/Card'
import { darkBlue } from 'app/theme'
import { defaultLotsFilter, getCustomerItems, LOT_STATUS, lotYears } from './LotsUtils'

const QUARTER_ITEMS = [1, 2, 3, 4].map(q => ({ value: q, label: `T${q}` }))

const PERIOD_TOOLTIP = (
    <span style={{ whiteSpace: 'pre-line' }}>
        {'Un lot est retenu si l\'une de ses notes d\'avancement ou son échéance tombe dans la période sélectionnée.\n'
            + 'Années et trimestres se combinent : 2026 + T1 ne retient que le 1er trimestre 2026 ; T1 seul retient le 1er trimestre de toutes les années.\n'
            + 'Sans sélection, pas de restriction.'}
    </span>
)

// pointerEvents : le label d'un TextField MUI peut ignorer la souris, l'icône doit la capter pour le survol.
const PeriodLabel = ({ label }) => (
    <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        {label}
        <Tooltip title={PERIOD_TOOLTIP}>
            <HelpIcon sx={{ fontSize: 16, color: '#000', pointerEvents: 'auto', cursor: 'help' }} />
        </Tooltip>
    </Box>
)
PeriodLabel.propTypes = { label: PropTypes.string }

// Encart de filtre : recherche libre + années / trimestres / statuts (+ clients si `customers` fourni,
// + chefs de projet si `chiefItems` fourni), en multi-sélection. Options d'années dérivées des lots.
const LotsFilterCard = ({ lots = [], customers = [], chiefItems = [], statuses = [], filter = {}, onChange = () => {}, view, onChangeView = () => {} }) => {
    const { search = '', years = [], quarters = [], statuts = [], clients = [], chiefs = [] } = filter
    const hasClientFilter = customers.length > 0
    const hasChiefFilter = chiefItems.length > 0
    const selectSize = 12 / (1 + Number(hasClientFilter) + Number(hasChiefFilter))

    const yearItems = useMemo(
        () => uniq([new Date().getFullYear(), ...lots.flatMap(lotYears)]).sort((a, b) => b - a).map(y => ({ value: y, label: `${y}` })),
        [lots],
    )
    const statutItems = LOT_STATUS.filter(s => statuses.includes(s.value)).map(s => ({
        value: s.value,
        label: s.label,
        icon: <Circle sx={{ color: s.color, fontSize: '1rem', marginRight: '5px', verticalAlign: 'middle' }} />,
    }))
    const clientItems = useMemo(() => getCustomerItems(customers), [customers])

    return (
        <Card sx={{ height: '100%' }}>
            <CardTitle title='Filtre' color={darkBlue} />
            <CardContent>
                <Grid container spacing={1} alignItems='center'>
                    <Grid size={6}>
                        <Input label='Rechercher' value={search} onChange={v => onChange({ ...filter, search: v })} />
                    </Grid>
                    <Grid size={3}>
                        <Select multiple label={<PeriodLabel label='Années' />} value={years} items={yearItems} onChange={v => onChange({ ...filter, years: v })} sortItems={false} />
                    </Grid>
                    <Grid size={3}>
                        <Select multiple label={<PeriodLabel label='Trimestres' />} value={quarters} items={QUARTER_ITEMS} onChange={v => onChange({ ...filter, quarters: v })} sortItems={false} />
                    </Grid>
                    <Grid size={selectSize}>
                        <Select multiple label='Statuts' value={statuts} items={statutItems} onChange={v => onChange({ ...filter, statuts: v })} sortItems={false} />
                    </Grid>
                    {hasClientFilter && (
                        <Grid size={selectSize}>
                            <Autocomplete multiple label='Clients' value={clients.filter(c => clientItems.some(i => i.id === c))} items={clientItems} onChange={v => onChange({ ...filter, clients: v })} sortItems={false} />
                        </Grid>
                    )}
                    {hasChiefFilter && (
                        <Grid size={selectSize}>
                            <Autocomplete multiple label='Chefs de projet' value={chiefs.filter(c => chiefItems.some(i => i.id === c))} items={chiefItems} onChange={v => onChange({ ...filter, chiefs: v })} sortItems={false} />
                        </Grid>
                    )}
                    <Grid size={12} container justifyContent='space-between' alignItems='center'>
                        <ToggleButtonGroup exclusive size='small' value={view} onChange={(_, v) => !isNil(v) && onChangeView(v)}>
                            <ToggleButton value='cards'><Icon fontSize='small' sx={{ mr: 0.5 }}>view_agenda</Icon>Cartes</ToggleButton>
                            <ToggleButton value='table'><Icon fontSize='small' sx={{ mr: 0.5 }}>table_rows</Icon>Tableau</ToggleButton>
                        </ToggleButtonGroup>
                        <Button variant='outlined' onClick={() => onChange(defaultLotsFilter())}>Réinitialiser</Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}
LotsFilterCard.propTypes = {
    lots: PropTypes.arrayOf(PropTypes.shape({})),
    customers: PropTypes.arrayOf(PropTypes.shape({})),
    chiefItems: PropTypes.arrayOf(PropTypes.shape({})),
    statuses: PropTypes.arrayOf(PropTypes.number),
    filter: PropTypes.shape({}),
    onChange: PropTypes.func,
    view: PropTypes.string,
    onChangeView: PropTypes.func,
}

export default LotsFilterCard
